/**
 * MiraContext — minimal shared open/close state so the trigger
 * button (in the navbar) and the drawer (mounted at the App root)
 * can talk without prop drilling. ~30 lines on purpose.
 */
import React, { createContext, useContext, useState } from "react";

type MiraCtx = {
  open: boolean;
  setOpen: (v: boolean) => void;
};

const Ctx = createContext<MiraCtx | null>(null);

export const MiraProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  return <Ctx.Provider value={{ open, setOpen }}>{children}</Ctx.Provider>;
};

/**
 * Returns the shared open/setOpen pair. Falls back to a noop pair when
 * called outside the provider so a component (e.g. a stray render in a
 * test) doesn't crash the page — the trigger and drawer just won't
 * coordinate, which is the right degradation for a non-critical
 * widget.
 */
export const useMira = (): MiraCtx => {
  const c = useContext(Ctx);
  if (c) return c;
  return { open: false, setOpen: () => {} };
};
